import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { api } from "../lib/api";
import { User as UserIcon, EnvelopeSimple, Phone, DeviceMobile, Buildings, MapPin, NotePencil, ShieldCheck, CalendarBlank } from "@phosphor-icons/react";

// v285 — Read-only fiche utilisateur pop-in used on the ticket detail page.
// Opened when the tech / admin clicks on the client name. Fetches
// `/users/{id}` on open so the info is always fresh (fixes the historical
// stale-name issue reported on 2026-02-27 where the ticket kept the old
// full_name after an admin renamed the user).
export default function UserInfoPopover({ userId, userName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true); setErr(null); setData(null);
    api.get(`/users/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Impossible de charger cet utilisateur"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const roleLabel = data?.role === "admin" ? "Administrateur"
    : data?.role === "technicien" ? "Technicien"
    : data?.role === "client" ? "Client" : "—";

  const fullName = data?.full_name || [data?.first_name, data?.last_name].filter(Boolean).join(" ") || userName || "—";
  const addr = [data?.address, data?.postal_code, data?.city, data?.country].filter(Boolean).join(", ");
  const created = data?.created_at ? new Date(data.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="user-info-popover">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UserIcon size={20}/> {fullName}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-muted-foreground py-6 text-center">Chargement…</div>}
        {err && <div className="text-sm text-destructive py-6 text-center">{err}</div>}
        {data && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" data-testid="user-info-role">{roleLabel}</Badge>
              {data.active === false
                ? <Badge className="bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-200">Désactivé</Badge>
                : <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200">Actif</Badge>}
              {data.email_notifications === false && (
                <Badge variant="secondary">Notifs email OFF</Badge>
              )}
            </div>
            {data.email && (
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={14} className="text-muted-foreground shrink-0"/>
                <a href={`mailto:${data.email}`} className="text-primary hover:underline break-all" data-testid="user-info-email">{data.email}</a>
              </div>
            )}
            {data.phone && (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-muted-foreground shrink-0"/>
                <a href={`tel:${data.phone.replace(/\s+/g,"")}`} className="text-emerald-700 dark:text-emerald-400 hover:underline mono" data-testid="user-info-phone">{data.phone}</a>
                <span className="text-xs text-muted-foreground">(fixe)</span>
              </div>
            )}
            {data.mobile_phone && (
              <div className="flex items-center gap-2">
                <DeviceMobile size={14} className="text-muted-foreground shrink-0"/>
                <a href={`tel:${data.mobile_phone.replace(/\s+/g,"")}`} className="text-emerald-700 dark:text-emerald-400 hover:underline mono" data-testid="user-info-mobile">{data.mobile_phone}</a>
                <span className="text-xs text-muted-foreground">(portable)</span>
              </div>
            )}
            {data.company && (
              <div className="flex items-center gap-2">
                <Buildings size={14} className="text-muted-foreground shrink-0"/>
                <span data-testid="user-info-company">{data.company}</span>
              </div>
            )}
            {addr && (
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-muted-foreground shrink-0 mt-0.5"/>
                <span className="text-muted-foreground" data-testid="user-info-address">{addr}</span>
              </div>
            )}
            {data.notes && (
              <div className="flex items-start gap-2 border-t pt-3">
                <NotePencil size={14} className="text-muted-foreground shrink-0 mt-0.5"/>
                <div className="text-muted-foreground whitespace-pre-wrap" data-testid="user-info-notes">{data.notes}</div>
              </div>
            )}
            {created && (
              <div className="flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
                <CalendarBlank size={12}/>
                Compte créé le {created}
              </div>
            )}
            {data.two_factor_enabled && (
              <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                <ShieldCheck size={12} weight="fill"/> 2FA activée
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
